import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getWalletPortfolio } from "../api/endpoints/wallet/wallet";
import type { PortfolioListItem } from "../api/models/portfolioListItem";
import { getCoins, type Coin } from "../lib/marketApi";
import { getBearerToken } from "../lib/authClient";
import { toNumber } from "../lib/numberFormat";

import "./PortfolioAssetsPage.css";

const formatUSD = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "USD",
  });

const formatQty = (v: string | number) =>
  toNumber(v).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  });

const formatSignedPct = (v: string | number) => {
  const n = toNumber(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};

export default function PortfolioAssetsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<PortfolioListItem[]>([]);
  const [coinsMeta, setCoinsMeta] = useState<Map<string, Coin>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = getBearerToken();
        const [portfolioRes, coins] = await Promise.all([
          getWalletPortfolio(
            token
              ? { headers: { Authorization: `Bearer ${token}` } }
              : undefined,
          ),
          getCoins().catch(() => [] as Coin[]),
        ]);
        if (cancelled) return;

        if (portfolioRes.status === 200 && "data" in portfolioRes.data) {
          setItems(portfolioRes.data.data);
        } else {
          setError(`Falha ao buscar portfólio (status ${portfolioRes.status}).`);
        }

        const map = new Map<string, Coin>();
        for (const c of coins) map.set(c.code.toUpperCase(), c);
        setCoinsMeta(map);
      } catch (err) {
        if (cancelled) return;
        console.error("Erro ao buscar portfólio:", err);
        setError(err instanceof Error ? err.message : "Erro desconhecido");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => items, [items]);

  const handleOpen = (item: PortfolioListItem) => {
    const meta = coinsMeta.get(item.coin.toUpperCase());
    navigate(`/wallet/portfolio/${item.coin}`, {
      state: {
        code: item.coin,
        name: meta?.name,
        icon: meta?.icon,
      },
    });
  };

  return (
    <div className="page-centered">
      <div className="portfolio-wrap">
        <div className="portfolio-header">
          <button
            type="button"
            className="portfolio-iconBtn"
            onClick={() => navigate(-1)}
            aria-label="Voltar"
          >
            ←
          </button>
          <div className="portfolio-title">Carteira</div>
          <button
            type="button"
            className="portfolio-iconBtn portfolio-iconBtn--bot"
            onClick={() => navigate("/chat")}
            aria-label="Chat IA"
          >
            🤖
          </button>
        </div>

        <div className="portfolio-sectionHead">
          <div className="portfolio-sectionTitle">Ativos</div>
        </div>

        <div className="portfolio-legend">
          <span className="portfolio-legend-item">
            <span className="portfolio-legend-dot portfolio-legend-dot--equity" />
            <strong>Equity</strong> — saldo bruto + PnL não realizado (valor real)
          </span>
          <span className="portfolio-legend-item">
            <span className="portfolio-legend-dot portfolio-legend-dot--wallet" />
            <strong>Wallet Balance</strong> — saldo bruto (sem PnL)
          </span>
        </div>

        {loading && (
          <div className="portfolio-status">Carregando ativos...</div>
        )}

        {error && !loading && (
          <div className="portfolio-status portfolio-status--error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <div className="portfolio-list">
            {visible.length === 0 && (
              <div className="portfolio-status">Nenhum ativo encontrado.</div>
            )}
            {visible.map((item) => {
              const meta = coinsMeta.get(item.coin.toUpperCase());
              const isUp = toNumber(item.change24hPercent) >= 0;
              return (
                <div
                  key={item.coin}
                  className="portfolio-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleOpen(item);
                    }
                  }}
                >
                  <div className="portfolio-item-icon">
                    {meta?.icon
                      ? <img src={meta.icon} alt={meta.name ?? item.coin} />
                      : item.coin.slice(0, 3)}
                  </div>
                  <div className="portfolio-item-info">
                    <div className="portfolio-item-code">{item.coin}</div>
                    <div className="portfolio-item-sub">
                      <span>{formatUSD(item.priceUSD)}</span>
                      <span
                        className={`portfolio-item-change ${
                          isUp
                            ? "portfolio-item-change--up"
                            : "portfolio-item-change--down"
                        }`}
                      >
                        {formatSignedPct(item.change24hPercent)}
                        {isUp ? " ↗" : " ↘"}
                      </span>
                    </div>
                  </div>
                  <div className="portfolio-item-values">
                    <div className="portfolio-item-valRow">
                      <span className="portfolio-item-valDot portfolio-legend-dot--equity" />
                      <span className="portfolio-item-qty">
                        {formatQty(item.equity)}
                      </span>
                    </div>
                    <div className="portfolio-item-valRow">
                      <span className="portfolio-item-valDot portfolio-legend-dot--wallet" />
                      <span className="portfolio-item-wallet">
                        {formatQty(item.walletBalance)}
                      </span>
                    </div>
                    <div className="portfolio-item-total">
                      {formatUSD(item.totalUSD)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
